import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  SlidersHorizontal, Award, Smile, Calendar, Droplets, Settings, Sparkles,
  Fish, FlaskConical, Hand, Heart, Wrench, ShieldCheck, Clock, Check,
  ArrowRight, ChevronRight, ChevronLeft, Plus, Minus, Star, Quote,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const TRANSITION_DURATION_MS = 1500;

const TRUST = [
  { icon: SlidersHorizontal, title: "Customized For Every Aquarium", body: "Every aquarium is different. We begin with a personal consultation and visual inspection to create a maintenance program tailored specifically to your system, livestock, and environment." },
  { icon: Award, title: "Experienced Professionals", body: "Our team has years of experience maintaining everything from freshwater aquariums to advanced reef ecosystems, delivering expert care with every visit." },
  { icon: Smile, title: "Complete Peace of Mind", body: "With scheduled service, proactive monitoring, and consistent communication, you'll always know your aquarium is receiving the attention it deserves." },
];

const INCLUDED = [
  { icon: Calendar, title: "Scheduled Maintenance Visits", body: "Routine service visits are typically performed twice each month to maintain optimal aquarium health and appearance." },
  { icon: Droplets, title: "Water Changes", body: "RODI water is used to maintain exceptional water quality and provide a healthier environment for your aquatic life." },
  { icon: Settings, title: "Equipment Inspection", body: "Filters, pumps, heaters, lighting, and life-support equipment are inspected to ensure reliable operation and identify potential issues before they become problems." },
  { icon: Sparkles, title: "Aquarium Cleaning", body: "Glass, décor, substrate, and filtration components are cleaned or replaced as needed to keep your aquarium looking its best." },
  { icon: Fish, title: "Fish Feeding & Care", body: "We provide quality frozen and flake foods while monitoring fish behavior, overall health, and feeding habits during every visit." },
  { icon: FlaskConical, title: "Water Testing", body: "Critical water parameters are monitored and adjusted to maintain long-term stability and a healthy aquatic ecosystem." },
  { icon: Hand, title: "White-Glove Finish", body: "Every maintenance visit concludes with a spotless work area, ensuring your home or office looks just as pristine as your aquarium." },
];

const FLOW = ["Consultation", "Inspection", "Custom Proposal", "Scheduled Service", "Ongoing Care"];

const MATTERS = [
  { icon: Heart, title: "Healthy Fish & Coral", body: "Consistent maintenance promotes healthier livestock and reduces stress caused by unstable water conditions." },
  { icon: Droplets, title: "Crystal Clear Water", body: "Professional filtration maintenance and regular water changes maintain exceptional water clarity." },
  { icon: Wrench, title: "Equipment Reliability", body: "Routine inspections help identify worn equipment before failures impact your aquarium." },
  { icon: FlaskConical, title: "Stable Water Chemistry", body: "Consistent monitoring helps maintain ideal parameters for fish, plants, and coral." },
  { icon: ShieldCheck, title: "Long-Term Investment Protection", body: "Regular preventative maintenance extends the lifespan of your equipment and helps protect your aquarium investment." },
  { icon: Clock, title: "More Time To Enjoy", body: "Spend less time maintaining your aquarium and more time enjoying the beauty of your living ecosystem." },
];

const DIFFERENT = [
  "Customized maintenance programs",
  "Experienced aquarium specialists",
  "Professional RODI water changes",
  "Premium fish food included",
  "Preventative equipment inspections",
  "White-glove service",
  "Ongoing communication",
  "Luxury residential and commercial experience",
];

const EXPECTATIONS = [
  { title: "Transparent Pricing", body: "Maintenance programs are billed at a flat monthly service rate based on your aquarium's size, complexity, and care requirements." },
  { title: "Replacement Equipment", body: "Consumable and replacement items—including filters, pumps, heaters, lighting, repairs, enhancements, and specialty equipment—are quoted separately when needed, ensuring complete transparency and no surprise charges." },
  { title: "Consistent Communication", body: "You'll always know the health of your aquarium, any recommended improvements, and any equipment that may need attention." },
];

const TESTIMONIALS = [
  { quote: "Seatech transformed our reef tank into the centerpiece of our home. Reliable, professional, and always a pleasure to work with.", name: "Marcus Reed", role: "Residential Client, Scottsdale" },
  { quote: "Twice-a-month visits keep our office aquarium flawless. We never think about maintenance anymore—that's the whole point.", name: "Dana Whitfield", role: "Office Manager, Phoenix" },
  { quote: "The white-glove finish is real. Spotless work area every visit and our fish have never looked healthier.", name: "Priya Anand", role: "Reef Aquarium Owner" },
  { quote: "Trustworthy, communicative, and genuinely expert. Seatech feels like a long-term partner, not a vendor.", name: "Carlos Mendez", role: "Restaurant Owner, Glendale" },
];

const GALLERY = [
  "https://images.pexels.com/photos/10431750/pexels-photo-10431750.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.pexels.com/photos/30590630/pexels-photo-30590630.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.pexels.com/photos/5472268/pexels-photo-5472268.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1691387824643-227cc84127cf?q=85&w=1000",
  "https://images.pexels.com/photos/37703360/pexels-photo-37703360.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1544552866-d3ed42536cfd?q=85&w=1000",
  "https://images.pexels.com/photos/17751990/pexels-photo-17751990.jpeg?auto=compress&cs=tinysrgb&w=1000",
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 44, filter: "blur(6px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Bubbles = () => {
  const bubbles = useMemo(
    () => Array.from({ length: 15 }).map((_, i) => ({
      id: i, left: Math.random() * 100, size: 5 + Math.random() * 18,
      duration: 11 + Math.random() * 12, delay: Math.random() * 10,
    })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bubbles.map((b) => (
        <motion.span
          key={b.id}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "-15%", opacity: [0, 0.5, 0.5, 0] }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: "linear" }}
          className="absolute rounded-full border border-[var(--aqua)]/30 bg-[var(--aqua)]/10 backdrop-blur-[1px]"
          style={{ left: `${b.left}%`, width: b.size, height: b.size }}
        />
      ))}
    </div>
  );
};

const LightRays = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    {[18, 50, 80].map((left, i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.04, 0.14, 0.04] }}
        transition={{ duration: 9 + i * 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-1/3 h-[160%] w-40 rotate-12 bg-gradient-to-b from-[#8ff0ea]/40 via-[var(--aqua)]/10 to-transparent blur-2xl"
        style={{ left: `${left}%` }}
      />
    ))}
  </div>
);

export default function ServiceMaintenance() {
  const [phase, setPhase] = useState("intro");
  const [openAcc, setOpenAcc] = useState(0);
  const [tIndex, setTIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, []);

  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("page"), TRANSITION_DURATION_MS);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    const id = setInterval(() => setTIndex((v) => (v + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  const goHome = (target) => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector(target);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 250);
  };

  return (
    <main data-testid="maintenance-page" className="relative min-h-screen bg-[var(--ink)] text-[var(--foam)]">
      {phase === "intro" && (
        <div className="fixed inset-0 z-[70] bg-[var(--ink)] cursor-pointer" data-testid="maintenance-intro" onClick={() => setPhase("page")}>
          <video
            className="absolute inset-0 h-full w-full object-cover"
            src="/media/maintenance-transition.mp4"
            autoPlay muted playsInline
            onEnded={() => setPhase("page")}
            data-testid="maintenance-transition-video"
          />
        </div>
      )}

      <div className="fixed inset-0 z-0">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="/media/maintenance-loop.mp4"
          autoPlay loop muted playsInline
          data-testid="maintenance-loop-video"
        />
        <div className="absolute inset-0 bg-[#070c1c]/90" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060a1a]/94 via-[#0d1330]/86 to-[#05091a]/97" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase === "page" ? 1 : 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10"
      >
        <Navbar />

        {/* HERO */}
        <section className="relative flex min-h-[58vh] items-center overflow-hidden px-6 lg:px-16 pt-52 pb-10">
          <LightRays />
          <Bubbles />
          <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[var(--aqua)]/40 bg-[var(--aqua)]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.28em] text-[#8ff0ea]" data-testid="maintenance-kicker">
                White-Glove Aquarium Care
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl" data-testid="maintenance-headline">
                Professional Aquarium<br />Service &amp; Maintenance
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[var(--foam-dim)] lg:text-lg">
                Protect your investment with customized maintenance programs designed to keep your
                aquarium healthy, beautiful, and thriving year round. We handle every detail so you can
                simply enjoy the view.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="maintenance-hero-cta"
                  className="group inline-flex items-center gap-3 rounded-full bg-[var(--aqua)] px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[var(--ink-900)] transition-all duration-300 hover:shadow-[0_0_40px_-8px_var(--aqua)]"
                >
                  Request a Maintenance Proposal
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => document.getElementById("included")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="maintenance-hero-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-6 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60"
                >
                  Explore Maintenance Plans
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* WHY CLIENTS TRUST SEATECH */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Clients Trust Seatech</h2></Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {TRUST.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.12}>
                  <div className="group h-full rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[var(--aqua)]/45" data-testid={`maintenance-trust-card-${i}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--aqua)]/25 bg-[var(--aqua)]/10 text-[#8ff0ea] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_var(--aqua)]">
                      <c.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-xl font-bold tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT'S INCLUDED */}
        <section id="included" className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#8ff0ea]">Recurring Workflow</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">What's Included</h2>
            </Reveal>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {INCLUDED.map((s, i) => (
                <Reveal key={s.title} delay={(i % 3) * 0.08}>
                  <div className="group h-full rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/45 p-7 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[var(--aqua)]/45" data-testid={`maintenance-included-${i}`}>
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[var(--aqua)]/25 bg-[var(--aqua)]/10 text-[#8ff0ea]">
                        <s.icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-title text-base font-bold leading-snug tracking-tight">{s.title}</h3>
                    </div>
                    <p className="mt-4 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{s.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* A CUSTOMIZED APPROACH */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#8ff0ea]">A Customized Approach</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">Every Aquarium Is Different</h2>
              <p className="mt-5 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                No two aquariums have identical equipment, livestock, or environmental conditions. That's
                why every maintenance proposal is customized after a professional consultation and visual
                inspection.
              </p>
            </Reveal>
            <div className="space-y-4">
              {FLOW.map((step, i) => (
                <Reveal key={step} delay={i * 0.1}>
                  <div className="flex items-center gap-5 rounded-2xl border border-[var(--foam)]/12 bg-[#0b1024]/45 px-6 py-5 backdrop-blur-2xl transition-all duration-500 hover:border-[var(--aqua)]/45" data-testid={`maintenance-flow-${i}`}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--aqua)]/30 bg-[var(--aqua)]/10 font-title text-sm font-bold text-[#8ff0ea]">{i + 1}</span>
                    <span className="font-title text-lg font-bold tracking-tight">{step}</span>
                    {i < FLOW.length - 1 && <ChevronRight className="ml-auto h-5 w-5 text-[var(--aqua)]/60" />}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHY PROFESSIONAL MAINTENANCE MATTERS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Professional Maintenance Matters</h2></Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {MATTERS.map((s, i) => (
                <Reveal key={s.title} delay={(i % 3) * 0.08}>
                  <div className="group h-full rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:scale-[1.02] hover:border-[var(--aqua)]/45 hover:shadow-[0_20px_60px_-30px_var(--aqua)]" data-testid={`maintenance-matters-${i}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--aqua)]/25 bg-[var(--aqua)]/10 text-[#8ff0ea]">
                      <s.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{s.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT MAKES SEATECH DIFFERENT */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-4xl">
            <Reveal>
              <div className="rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/45 p-10 backdrop-blur-2xl" data-testid="maintenance-different">
                <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">What Makes Seatech Different</h2>
                <p className="mt-2 font-body text-sm font-medium uppercase tracking-[0.2em] text-[#8ff0ea]">The Seatech Experience</p>
                <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                  {DIFFERENT.map((d) => (
                    <li key={d} className="flex items-center gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--aqua)]/30 bg-[var(--aqua)]/10 text-[#8ff0ea]"><Check className="h-4 w-4" /></span>
                      <span className="font-body text-sm font-light text-[var(--foam)]">{d}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-10 border-t border-[var(--foam)]/10 pt-8 font-title text-lg font-bold leading-snug tracking-tight lg:text-xl">
                  Our goal is simple: to make aquarium ownership effortless while helping your aquatic
                  ecosystem thrive for years to come.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* SERVICE EXPECTATIONS (accordion) */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-4xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Service Expectations</h2></Reveal>
            <div className="mt-10 space-y-4">
              {EXPECTATIONS.map((e, i) => {
                const open = openAcc === i;
                return (
                  <Reveal key={e.title} delay={i * 0.08}>
                    <div className="overflow-hidden rounded-2xl border border-[var(--foam)]/12 bg-[#0b1024]/45 backdrop-blur-2xl" data-testid={`maintenance-expectation-${i}`}>
                      <button
                        onClick={() => setOpenAcc(open ? -1 : i)}
                        data-testid={`maintenance-expectation-toggle-${i}`}
                        className="flex w-full items-center justify-between gap-4 px-7 py-6 text-left transition-colors hover:bg-[var(--aqua)]/5"
                      >
                        <span className="font-title text-lg font-bold tracking-tight">{e.title}</span>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--aqua)]/30 bg-[var(--aqua)]/10 text-[#8ff0ea]">
                          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                          >
                            <p className="px-7 pb-6 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{e.body}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative px-6 lg:px-16 py-32">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030616]/80 to-[#030616]/95" />
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="maintenance-final-heading">Leave the Maintenance to the Experts</h2>
              <p className="mx-auto mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                Whether you own a freshwater aquarium, saltwater system, or advanced reef display, Seatech
                Aquariums provides customized maintenance programs that protect your investment, support
                healthy aquatic life, and give you complete peace of mind.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button onClick={() => goHome("#contact")} data-testid="maintenance-final-cta" className="inline-flex items-center gap-3 rounded-full bg-[var(--aqua)] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[var(--ink-900)] transition-all duration-300 hover:shadow-[0_0_44px_-8px_var(--aqua)]">Request Your Custom Maintenance Proposal</button>
                <button onClick={() => goHome("#contact")} data-testid="maintenance-final-secondary" className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60">Schedule a Consultation</button>
              </div>
            </Reveal>
          </div>
        </section>
      </motion.div>
    </main>
  );
}
